import React, {useCallback, useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {
  AgendaSettings,
  AgendaListProps,
} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/AgendaList'
import {useSimpleBlogTemplate} from 'Event/template/SimpleBlog'
import ComponentConfig, {
  ComponentConfigProps,
  SaveButton,
  RemoveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import Settings from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/AgendaList/AgendaItemConfig/Settings'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import {useAddTranslation, useRemoveTranslations} from 'Event/LanguageProvider'
import {generateHashId} from 'lib/crypto/hash'
import {Rule} from 'Event/attendee-rules'

export function AgendaItemConfig(
  props: {
    agenda: AgendaSettings
    id?: string
    listId: string
  } & ComponentConfigProps,
) {
  const {showing, onClose, agenda, id, listId} = props
  const {sidebarItems} = useSimpleBlogTemplate()
  const saveTemplate = useSaveTemplate()
  const addTranslation = useAddTranslation()
  const removeTranslations = useRemoveTranslations()
  const [rules, setRules] = useState<Rule[]>(agenda.rules || [])

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState,
    errors,
  } = useForm()

  useEffect(() => {
    if (showing) {
      return
    }

    setRules(agenda.rules || [])
  }, [agenda, showing])

  const update = (id: string, updated: AgendaSettings) => {
    const text = replaceAtPath(updated, 'text', `{{${id}_text}}`)
    const link = replaceAtPath(updated, 'link', `{{${id}_link}}`)

    saveTemplate({
      sidebarItems: {
        [listId]: {
          items: {
            [id]: updated,
          },
        },
      },
      localization: addTranslation({
        [`${id}_text`]: text ?? '',
        [`${id}_link`]: link ?? '',
      }),
    })
  }

  const insert = (item: AgendaSettings) => {
    const list = sidebarItems[listId] as AgendaListProps
    const position = Object.keys(list.items).length + 1

    generateHashId([
      'simple_blog_sidebar_agenda_item',
      new Date().valueOf().toString(),
      item.position?.toString() ?? '',
      Math.random().toString(),
    ]).then((id) => {
      const componentId = `agenda_${id}`
      update(componentId, {
        ...item,
        position,
      })
    })
  }

  const save = (formData: AgendaSettings) => {
    const data: AgendaSettings = {
      ...formData,
      rules,
    }

    if (id === undefined) {
      insert(data)
      onClose()
      return
    }

    update(id, data)
    onClose()
  }

  const remove = useCallback(() => {
    if (!id) {
      throw new Error("Missing 'id' of agenda to remove.")
    }

    saveTemplate({
      sidebarItems: {
        [listId]: {
          items: {
            [id]: REMOVE,
          },
        },
      },
      localization: removeTranslations([`${id}_text`]),
    })

    onClose()
  }, [id, saveTemplate, listId, removeTranslations, onClose])

  return (
    <ComponentConfig
      showing={showing}
      onClose={onClose}
      title="Agenda"
      onSubmit={handleSubmit(save)}
      hasChanges={formState.isDirty}
    >
      <SettingsPanel>
        <Settings
          control={control}
          register={register}
          agenda={agenda}
          watch={watch}
          setValue={setValue}
          errors={errors}
        />
      </SettingsPanel>
      <RulesPanel rules={rules} setRules={setRules} />
      <Footer>
        <SaveButton type="submit" />
        <RemoveButton
          fullWidth
          variant="outlined"
          aria-label="remove agenda"
          onClick={remove}
          hidden={!id}
        >
          REMOVE AGENDA
        </RemoveButton>
      </Footer>
    </ComponentConfig>
  )
}
